/**
 * One row of a contact's timeline.
 *
 * A message is a bubble: outbound on the right, inbound on the left. The
 * direction is also in words, in the meta line ("You" or the contact's name)
 * and in screen-reader-only text before the body ("You sent" / "{name}
 * said"), so it does not rest on position alone.
 * An activity is a compact full-width row with its type's icon; a scheduled
 * activity that is not done yet is tinted and labelled "Scheduled".
 *
 * Either kind shows a chip naming its lead when it carries one, and an
 * imported message shows an "Imported" tag with the time it was sent. A
 * message recorded from the realtor's connected WhatsApp, live or from the
 * earlier chats, shows a "From WhatsApp" tag; its body is shown as stored,
 * placeholders such as "[Photo] caption" included.
 */

import {
	CalendarClock,
	HandCoins,
	MessageCircle,
	Phone,
	StickyNote,
	Trash2,
} from "lucide-react";
import type { ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { ActivityType } from "@/features/realtor/services/leads-api.service";
import type {
	TimelineEntry as Entry,
	TimelineLead,
} from "@/features/realtor/services/realtor-crm-api.service";
import { clockTime, dateTime } from "../messages/message-format";

// Exhaustive, so a new activity type without an icon fails to compile.
const ACTIVITY_ICONS: Record<
	ActivityType,
	ComponentType<{ className?: string }>
> = {
	call: Phone,
	viewing: HandCoins,
	message: MessageCircle,
	note: StickyNote,
	reminder: CalendarClock,
};

export function TimelineEntry({
	entry,
	contactName,
	onDeleteActivity,
}: {
	entry: Entry;
	contactName: string;
	/** Offered on activity rows only, and only when given. */
	onDeleteActivity?: (entry: Entry) => void;
}) {
	return entry.kind === "message" ? (
		<MessageBubble entry={entry} contactName={contactName} />
	) : (
		<ActivityRow entry={entry} onDelete={onDeleteActivity} />
	);
}

function MessageBubble({
	entry,
	contactName,
}: {
	entry: Entry;
	contactName: string;
}) {
	const { t, i18n } = useTranslation();
	const outbound = entry.direction === "outbound";
	const imported = entry.source === "import";
	const fromWhatsapp =
		entry.source === "whatsapp" || entry.source === "whatsapp_history";
	return (
		<li
			className={`flex ${outbound ? "justify-end" : "justify-start"}`}
			data-testid="timeline-message"
			data-direction={entry.direction}
		>
			<div
				className={`max-w-[85%] rounded-2xl border px-3 py-2 sm:max-w-[70%] ${
					outbound
						? "rounded-br-sm border-transparent bg-dashboard-pill-active-bg"
						: "rounded-bl-sm border-dashboard-border bg-dashboard-bg"
				}`}
			>
				<span className="sr-only" data-testid="timeline-direction">
					{outbound
						? t("realtor.timeline.fromYou")
						: t("realtor.timeline.fromThem", { name: contactName })}
				</span>
				{/* data-sensitive: masked in session replay — free text with a
				    buyer, who never saw the recording toggle. See lib/posthog.ts. */}
				<p
					data-sensitive
					className="whitespace-pre-wrap break-words text-sm text-dashboard-text"
				>
					{entry.body}
				</p>
				<div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] text-dashboard-muted">
					<span>
						{outbound ? t("realtor.messages.you") : contactName}
						{entry.channel && (
							<>
								{" · "}
								{t(`realtor.messages.channels.${entry.channel}`)}
							</>
						)}
						{!imported && (
							<>
								{" · "}
								<time dateTime={entry.occurredAt}>
									{clockTime(entry.occurredAt, i18n.language)}
								</time>
							</>
						)}
					</span>
					{imported && (
						<span data-testid="timeline-imported">
							<span className="rounded bg-dashboard-border px-1.5 py-0.5 font-semibold uppercase tracking-wider text-dashboard-text">
								{t("realtor.timeline.imported")}
							</span>{" "}
							<time dateTime={entry.occurredAt}>
								{t("realtor.timeline.sentAt", {
									time: dateTime(entry.occurredAt, i18n.language),
								})}
							</time>
						</span>
					)}
					{fromWhatsapp && (
						<span
							data-testid="timeline-whatsapp"
							className="rounded bg-dashboard-border px-1.5 py-0.5 font-semibold uppercase tracking-wider text-dashboard-text"
						>
							{t("realtor.timeline.fromWhatsapp")}
						</span>
					)}
					{entry.lead && <LeadChip lead={entry.lead} />}
				</div>
			</div>
		</li>
	);
}

function ActivityRow({
	entry,
	onDelete,
}: {
	entry: Entry;
	onDelete?: (entry: Entry) => void;
}) {
	const { t, i18n } = useTranslation();
	const type = entry.activityType ?? "note";
	const Icon = ACTIVITY_ICONS[type];
	const upcoming = entry.scheduledAt !== null && entry.completedAt === null;
	return (
		<li
			data-testid="timeline-activity"
			data-upcoming={upcoming || undefined}
			className={`flex items-start gap-3 rounded-md border p-3 ${
				upcoming
					? "border-dashboard-pill-awaiting bg-dashboard-pill-awaiting-bg"
					: "border-dashboard-border bg-dashboard-paper"
			}`}
		>
			<div className="shrink-0 rounded-full bg-dashboard-surface p-2">
				<Icon className="h-3.5 w-3.5 text-dashboard-muted" />
			</div>
			<div className="min-w-0 flex-1">
				<p className="truncate text-sm font-medium text-dashboard-text">
					{entry.title}
				</p>
				<div className="flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[11px] text-dashboard-muted">
					<span>
						{t(`realtor.leads.activityTypes.${type}`)}
						{" · "}
						{upcoming && (
							<>
								<span className="font-semibold text-dashboard-pill-awaiting">
									{t("realtor.timeline.scheduled")}
								</span>{" "}
							</>
						)}
						<time dateTime={entry.occurredAt}>
							{dateTime(entry.occurredAt, i18n.language)}
						</time>
					</span>
					{entry.lead && <LeadChip lead={entry.lead} />}
				</div>
				{entry.body && (
					<p
						data-sensitive
						className="mt-1 whitespace-pre-line text-xs text-dashboard-muted"
					>
						{entry.body}
					</p>
				)}
			</div>
			{onDelete && (
				<button
					type="button"
					onClick={() => onDelete(entry)}
					aria-label={t("realtor.leads.detail.deleteActivityAriaLabel")}
					className="shrink-0 text-dashboard-muted transition-opacity hover:opacity-70"
				>
					<Trash2 className="h-4 w-4" />
				</button>
			)}
		</li>
	);
}

function LeadChip({ lead }: { lead: TimelineLead }) {
	const { t } = useTranslation();
	return (
		<Link
			to={`/realtor/leads/${lead.id}`}
			data-testid="timeline-lead-chip"
			aria-label={t("realtor.timeline.leadChipAria", { name: lead.name })}
			className="inline-flex max-w-[12rem] items-center truncate rounded-full border border-dashboard-border bg-dashboard-surface px-2 py-0.5 font-sans text-[10px] font-medium text-dashboard-text hover:bg-dashboard-bg"
		>
			{lead.name}
		</Link>
	);
}
